/**
 * Quick Order Dialog — Redesigned
 *
 * 3-column layout:
 *   Col 1 | Instrument details + product type
 *   Col 2 | BUY/SELL · Category tabs · Order type radios · Price fields · TIF
 *   Col 3 | Broker accounts (greyed if order type unsupported)
 *
 * Bottom bar: live order summary + Place Order button
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Checkbox,
  Card,
  CardContent,
  Tooltip,
  Chip,
  Divider,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useMultiBrokerOrder } from '@shared/features/orders';
import { useBrokerAccountSelector } from '@shared/features/broker-accounts';
import { Instrument } from '@shared/types/instrument';
import { formatCurrency } from '@shared/utils/formatters';
import { extractOrderError } from '@shared/types/order';
import {
  ORDER_TYPES,
  TIME_IN_FORCE,
  TIME_IN_FORCE_OPTIONS,
  ORDER_CATEGORY_MAP,
  OrderCategory,
  brokerSupportsOrderType,
  NOT_YET_IMPLEMENTED_ORDER_TYPES,
  ORDER_TYPE_VALID_TIF,
} from '@shared/constants/orderConstants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PRODUCT_TYPES = ['CNC', 'MIS', 'NRML'] as const;
type ProductType = typeof PRODUCT_TYPES[number];

const CATEGORIES: OrderCategory[] = ['simple', 'advanced', 'complex'];
const CATEGORY_LABELS: Record<OrderCategory, string> = {
  simple: 'Simple',
  advanced: 'Advanced',
  complex: 'Complex',
};

// Human-readable labels + short descriptions for each order type
const ORDER_TYPE_META: Record<string, { label: string; hint: string }> = {
  market: { label: 'Market', hint: 'Fill instantly at best price' },
  limit: { label: 'Limit', hint: 'Fill at specified price or better' },
  stop: { label: 'Stop', hint: 'Trigger a market order at stop price' },
  stop_limit: { label: 'Stop Limit', hint: 'Trigger a limit order at stop price' },
  iceberg: { label: 'Iceberg', hint: 'Show only disclosed qty at a time' },
  trailing_stop: { label: 'Trailing Stop', hint: 'Stop that trails price by fixed offset' },
  twap: { label: 'TWAP', hint: 'Time-weighted avg price execution' },
  vwap: { label: 'VWAP', hint: 'Volume-weighted avg price execution' },
  bracket: { label: 'Bracket', hint: 'Entry + stop-loss + take-profit' },
  oco: { label: 'OCO', hint: 'One-Cancels-Other order pair' },
};

function isBuySide(side: string) {
  return ['buy', 'buy_to_open', 'buy_to_close', 'buy_to_cover', 'cover'].includes(side);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface QuickOrderDialogProps {
  open: boolean;
  onClose: () => void;
  instrument: Instrument | null;
  side: 'buy' | 'sell';
  currentPrice?: number;
}

interface OrderResult {
  accountId: string;
  accountName: string;
  brokerName: string;
  success: boolean;
  orderStatus?: string;
  message?: string;
  userMessage?: string;
  nextSteps?: string;
  isValidationError?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QuickOrderDialog({ open, onClose, instrument, side, currentPrice }: QuickOrderDialogProps) {
  // ── Order state ───────────────────────────────────────────────────────────
  const [selectedSide, setSelectedSide] = useState<string>(side);
  const [activeCategory, setActiveCategory] = useState<OrderCategory>('simple');
  const [orderType, setOrderType] = useState<string>(ORDER_TYPES.MARKET);
  const [productType, setProductType] = useState<ProductType>('CNC');
  const [timeInForce, setTimeInForce] = useState<string>(TIME_IN_FORCE.DAY);

  // ── Price fields ──────────────────────────────────────────────────────────
  const [price, setPrice] = useState<string>('');
  const [stopPrice, setStopPrice] = useState<string>('');
  const [trailingAmount, setTrailingAmount] = useState<string>('');
  const [profitTarget, setProfitTarget] = useState<string>('');
  const [stopLoss, setStopLoss] = useState<string>('');
  const [disclosedQuantity, setDisclosedQuantity] = useState<string>('');

  // ── Account selection ─────────────────────────────────────────────────────
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [accountQuantities, setAccountQuantities] = useState<Record<string, string>>({});
  const [qtyErrors, setQtyErrors] = useState<Record<string, string>>({});
  const [orderResults, setOrderResults] = useState<OrderResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  const { accounts } = useBrokerAccountSelector({ activeOnly: false });
  const { placeSingleOrder, placingOrder } = useMultiBrokerOrder();

  // ── Derived ───────────────────────────────────────────────────────────────

  const isConnected = useCallback(
    (account: any) => account.status?.toLowerCase() === 'connected' || account.active,
    [],
  );

  /** True if the account is connected AND the broker supports the current order type */
  const isEligible = useCallback(
    (account: any) => isConnected(account) && brokerSupportsOrderType(account.broker_name, orderType),
    [isConnected, orderType],
  );

  const needsLimitPrice = ['limit', 'stop_limit', 'loo', 'loc'].includes(orderType);
  const needsStopPrice = ['stop', 'stop_limit', 'trailing_stop'].includes(orderType);
  const isBracket = orderType === 'bracket';
  const isIceberg = orderType === 'iceberg';
  const isTrailing = orderType === 'trailing_stop';
  const isMarket = orderType === 'market';
  const buyingSide = isBuySide(selectedSide);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Auto-fill limit price with current price on first open
  useEffect(() => {
    if (needsLimitPrice && currentPrice && currentPrice > 0 && !price) {
      setPrice(currentPrice.toFixed(2));
    }
  }, [needsLimitPrice, currentPrice, price]);

  // Auto-select all connected+eligible accounts when dialog opens
  useEffect(() => {
    if (open && accounts.length > 0 && selectedAccountIds.length === 0) {
      const eligible = accounts.filter(isEligible).map(a => a.id);
      setSelectedAccountIds(eligible);
      const qtys: Record<string, string> = {};
      eligible.forEach(id => { qtys[id] = ''; });
      setAccountQuantities(qtys);
    }
  }, [open, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-deselect ineligible accounts when order type changes
  useEffect(() => {
    setSelectedAccountIds(prev =>
      prev.filter(id => {
        const acc = accounts.find(a => a.id === id);
        return acc && isEligible(acc);
      }),
    );
  }, [orderType, accounts, isEligible]);

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setSelectedSide(side);
      setActiveCategory('simple');
      setOrderType(ORDER_TYPES.MARKET);
      setProductType('CNC');
      setTimeInForce(TIME_IN_FORCE.DAY);
      setPrice('');
      setStopPrice('');
      setTrailingAmount('');
      setProfitTarget('');
      setStopLoss('');
      setDisclosedQuantity('');
      setSelectedAccountIds([]);
      setAccountQuantities({});
      setQtyErrors({});
      setOrderResults([]);
      setShowResults(false);
    }
  }, [open, side]);

  // Auto-reset TIF when switching order types if current TIF is inapplicable
  useEffect(() => {
    const validTif = ORDER_TYPE_VALID_TIF[orderType];
    if (validTif && !validTif.values.includes(timeInForce)) {
      setTimeInForce('day');
    }
  }, [orderType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCategoryChange = (cat: OrderCategory) => {
    setActiveCategory(cat);
    // Pick the first selectable (non-NYI) type in the new category
    const types = ORDER_CATEGORY_MAP[cat] as readonly string[];
    const firstSelectable = types.find(t => !NOT_YET_IMPLEMENTED_ORDER_TYPES.has(t)) ?? types[0];
    setOrderType(firstSelectable);
  };

  const handleAccountToggle = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account || !isEligible(account)) return;
    if (selectedAccountIds.includes(accountId)) {
      setSelectedAccountIds(prev => prev.filter(id => id !== accountId));
      setAccountQuantities(prev => { const u = { ...prev }; delete u[accountId]; return u; });
    } else {
      setSelectedAccountIds(prev => [...prev, accountId]);
      setAccountQuantities(prev => ({ ...prev, [accountId]: prev[accountId] || '' }));
    }
  };

  const handleSelectAll = () => {
    const eligible = accounts.filter(isEligible);
    const allSelected = eligible.every(a => selectedAccountIds.includes(a.id));
    if (allSelected) {
      setSelectedAccountIds([]);
      setAccountQuantities({});
    } else {
      const ids = eligible.map(a => a.id);
      setSelectedAccountIds(ids);
      setAccountQuantities(prev => {
        const u = { ...prev };
        ids.forEach(id => { if (!u[id]) u[id] = ''; });
        return u;
      });
    }
  };

  const handleQuantityChange = (accountId: string, qty: string) => {
    setAccountQuantities(prev => ({ ...prev, [accountId]: qty }));
    // Clear inline error as soon as the user types a valid value
    const parsed = parseFloat(qty);
    if (!isNaN(parsed) && Number.isInteger(parsed) && parsed >= 1) {
      setQtyErrors(prev => { const u = { ...prev }; delete u[accountId]; return u; });
    }
  };

  const applyPriceHelper = (type: 'ltp' | 'plus' | 'minus') => {
    if (!currentPrice) return;
    const val =
      type === 'ltp' ? currentPrice :
        type === 'plus' ? currentPrice * 1.005 :
          currentPrice * 0.995;
    setPrice(val.toFixed(2));
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateForm = (): string | null => {
    if (!instrument) return 'No instrument selected';
    if (selectedAccountIds.length === 0) return 'Please select at least one broker account';

    // Validate quantities — collect per-field errors rather than a single message
    const newQtyErrors: Record<string, string> = {};
    for (const id of selectedAccountIds) {
      const raw = accountQuantities[id] ?? '';
      const qty = parseFloat(raw);
      if (raw === '' || isNaN(qty) || qty < 1 || !Number.isInteger(qty)) {
        newQtyErrors[id] = 'Need QTY ≥ 1';
      }
    }
    if (Object.keys(newQtyErrors).length > 0) {
      setQtyErrors(newQtyErrors);
      return '__qty_field_error__'; // sentinel — caller must NOT route this to results panel
    }
    setQtyErrors({});

    if (needsLimitPrice && (!price || parseFloat(price) <= 0)) return 'Enter a valid limit price';
    if (needsStopPrice && (!stopPrice || parseFloat(stopPrice) <= 0)) return 'Enter a valid stop price';
    if (isTrailing && !trailingAmount) return 'Enter trailing amount';
    if (isBracket) {
      if (!profitTarget || parseFloat(profitTarget) <= 0) return 'Enter a valid profit target';
      if (!stopLoss || parseFloat(stopLoss) <= 0) return 'Enter a valid stop loss';
    }
    if (isIceberg && (!disclosedQuantity || parseFloat(disclosedQuantity) <= 0)) return 'Enter valid disclosed quantity';
    return null;
  };

  // ── Live summary ──────────────────────────────────────────────────────────

  const totalQty = useMemo(() =>
    selectedAccountIds.reduce((sum, id) => sum + (parseFloat(accountQuantities[id] || '0') || 0), 0),
    [selectedAccountIds, accountQuantities],
  );

  const orderSummary = useMemo(() => {
    if (!instrument) return '';
    const typeLabel = ORDER_TYPE_META[orderType]?.label ?? orderType.toUpperCase();
    const priceStr = needsLimitPrice && price ? ` @ ${formatCurrency(parseFloat(price))}` : isMarket ? ' @ Market' : '';
    const accounts = selectedAccountIds.length;
    return `${selectedSide.toUpperCase().replace('_', ' ')}  ${totalQty > 0 ? totalQty + ' ×' : ''}  ${instrument.symbol}${priceStr}  ·  ${typeLabel}  ·  ${productType}  ·  ${TIME_IN_FORCE_OPTIONS.find(o => o.value === timeInForce)?.label ?? timeInForce}${accounts > 0 ? `  ·  ${accounts} account${accounts > 1 ? 's' : ''}` : ''}`;
  }, [instrument, orderType, price, needsLimitPrice, isMarket, selectedSide, totalQty, productType, timeInForce, selectedAccountIds.length]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handlePlaceOrder = async () => {
    const validationError = validateForm();
    if (validationError) {
      // Quantity field errors are shown inline — don't open the results panel
      if (validationError === '__qty_field_error__') return;
      setOrderResults([{
        accountId: '__validation__',
        accountName: '',
        brokerName: '',
        success: false,
        message: validationError,
        isValidationError: true,
      }]);
      setShowResults(true);
      return;
    }

    const results: OrderResult[] = await Promise.all(
      selectedAccountIds.map(async (accountId) => {
        const account = accounts.find(a => a.id === accountId)!;
        try {
          const order = await placeSingleOrder({
            instrument_id: instrument!.instrument_id,
            side: selectedSide as any,
            quantity: (parseFloat(accountQuantities[accountId] || '1')).toString(),
            order_type: orderType as any,
            time_in_force: timeInForce as any,
            price: needsLimitPrice ? price : undefined,
            stop_price: needsStopPrice ? stopPrice : undefined,
            registration_id: account.id,
          });
          return {
            accountId,
            accountName: account.account_name || account.broker_account_id,
            brokerName: account.broker_name,
            success: true,
            orderStatus: order.status,
            message: order.message || 'Order placed successfully',
          } as OrderResult;
        } catch (err) {
          const orderError = extractOrderError(err);
          return {
            accountId,
            accountName: account.account_name || account.broker_account_id,
            brokerName: account.broker_name,
            success: false,
            message: orderError.message,
            userMessage: orderError.details?.userMessage,
            nextSteps: orderError.details?.nextSteps,
          } as OrderResult;
        }
      })
    );

    setOrderResults(results);
    setShowResults(true);
  };

  if (!instrument) return null;

  // ── Styled constants ──────────────────────────────────────────────────────

  const BUY_COLOR = '#16a34a';
  const SELL_COLOR = '#dc2626';
  const activeColor = buyingSide ? BUY_COLOR : SELL_COLOR;

  // ── Render ────────────────────────────────────────────────────────────────

  // eligible connected accounts for select-all count
  const eligibleAccounts = accounts.filter(isEligible);
  const allEligibleSelected = eligibleAccounts.length > 0 && eligibleAccounts.every(a => selectedAccountIds.includes(a.id));
  const someEligibleSelected = eligibleAccounts.some(a => selectedAccountIds.includes(a.id)) && !allEligibleSelected;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', maxWidth: 820 } }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(4, 55, 123, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#04377b' }}>
          Place Order
        </Typography>

        <Typography variant="caption" sx={{ color: '#04377b', opacity: 0.6, fontSize: '0.65rem' }}>
          {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 1.5, overflow: showResults ? 'auto' : 'hidden' }}>
        {showResults ? (
          /* ── ORDER RESULT PANEL ──────────────────────────────────────────── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Summary bar */}
            {(() => {
              const isValidationOnly = orderResults.length === 1 && orderResults[0].isValidationError;
              const successCount = orderResults.filter(r => r.success).length;
              const failCount = orderResults.length - successCount;
              const allOk = failCount === 0;
              return (
                <Alert
                  severity={isValidationOnly ? 'warning' : allOk ? 'success' : successCount > 0 ? 'warning' : 'error'}
                  sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}
                >
                  {isValidationOnly
                    ? 'Please fix the following before placing your order'
                    : allOk
                      ? `${successCount} order${successCount !== 1 ? 's' : ''} placed successfully`
                      : successCount > 0
                        ? `${successCount} placed · ${failCount} failed`
                        : `All ${failCount} order${failCount !== 1 ? 's' : ''} failed`}
                </Alert>
              );
            })()}

            {/* Per-account rows */}
            {orderResults.map(r => (
              <Box
                key={r.accountId}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: r.success ? '#bbf7d0' : '#fecaca',
                  bgcolor: r.success ? '#f0fdf4' : '#fef2f2',
                }}
              >
                {r.success
                  ? <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a', mt: '2px', flexShrink: 0 }} />
                  : <CancelIcon sx={{ fontSize: 16, color: '#dc2626', mt: '2px', flexShrink: 0 }} />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {!r.isValidationError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize' }}>
                        {r.brokerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.63rem' }}>
                        {r.accountName}
                      </Typography>
                      {r.success && r.orderStatus && (
                        <Chip
                          label={r.orderStatus.toUpperCase()}
                          size="small"
                          sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: r.success ? '#166534' : '#dc2626', display: 'block' }}>
                    {r.userMessage || r.message}
                  </Typography>
                  {r.nextSteps && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.61rem', display: 'block', mt: 0.25 }}>
                      ↳ {r.nextSteps}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 1.5, alignItems: 'stretch' }}>

            {/* ════════════════════════════════════════════════════════════════
              COLUMN 1 — Instrument Details + Product Type
          ════════════════════════════════════════════════════════════════ */}
            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, height: '100%' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 }, display: 'flex', flexDirection: 'column', gap: 1 }}>

                {/* Symbol + exchange */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>
                      {instrument.symbol}
                    </Typography>
                    {instrument.exchange && (
                      <Chip
                        label={instrument.exchange}
                        size="small"
                        sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#f3f4f6', border: '1px solid #e5e7eb' }}
                      />
                    )}
                    {instrument.option_type && (
                      <Chip
                        label={instrument.option_type}
                        size="small"
                        sx={{
                          fontSize: '0.6rem', height: 16,
                          bgcolor: instrument.option_type === 'CE' ? '#dcfce7' : '#fee2e2',
                          color: instrument.option_type === 'CE' ? '#15803d' : '#b91c1c',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  {instrument.name && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                      {instrument.name}
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* Instrument metadata */}
                <Stack spacing={0.5}>
                  {[
                    { label: 'Type', value: instrument.instrument_type ?? '—' },
                    { label: 'Segment', value: instrument.exchange ?? '—' },
                    ...(instrument.lot_size ? [{ label: 'Lot Size', value: String(instrument.lot_size) }] : []),
                    ...(instrument.tick_size ? [{ label: 'Tick Size', value: String(instrument.tick_size) }] : []),
                    ...(instrument.expiry_date ? [{ label: 'Expiry', value: instrument.expiry_date }] : []),
                    ...(instrument.strike_price ? [{ label: 'Strike', value: formatCurrency(instrument.strike_price) }] : []),
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{label}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>{value}</Typography>
                    </Box>
                  ))}
                </Stack>

                {/* LTP */}
                {currentPrice != null && (
                  <>
                    <Divider />
                    <Box sx={{ textAlign: 'center', py: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem', color: activeColor }}>
                        {formatCurrency(currentPrice)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        Last Traded Price
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                {/* Product Type */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    PRODUCT TYPE
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {PRODUCT_TYPES.map(pt => (
                      <Chip
                        key={pt}
                        label={pt}
                        size="small"
                        clickable
                        onClick={() => setProductType(pt)}
                        sx={{
                          fontSize: '0.65rem',
                          height: 22,
                          fontWeight: 600,
                          border: '1px solid',
                          borderColor: productType === pt ? activeColor : '#e5e7eb',
                          bgcolor: productType === pt ? (buyingSide ? '#dcfce7' : '#fee2e2') : '#f9fafb',
                          color: productType === pt ? activeColor : 'text.secondary',
                          '&:hover': { bgcolor: productType === pt ? undefined : '#f3f4f6' },
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.5, display: 'block' }}>
                    {productType === 'CNC' ? 'Delivery — carry forward overnight' :
                      productType === 'MIS' ? 'Intraday — auto square-off at 3:20 PM' :
                        'F&O carry-forward / commodities'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* ════════════════════════════════════════════════════════════════
              COLUMN 2 — Order Details (hierarchical layers)
          ════════════════════════════════════════════════════════════════ */}
            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* ── LAYER 1: BUY / SELL ─────────────────────────────────── */}
              <Box sx={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                {(['buy', 'sell'] as const).map(s => (
                  <Box
                    key={s}
                    onClick={() => setSelectedSide(s)}
                    sx={{
                      flex: 1,
                      py: 0.875,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      transition: 'all 0.15s',
                      color: selectedSide === s
                        ? '#fff'
                        : s === 'buy' ? '#16a34a' : '#dc2626',
                      bgcolor: selectedSide === s
                        ? (s === 'buy' ? '#16a34a' : '#dc2626')
                        : '#fafafa',
                      borderRight: s === 'buy' ? '1px solid #e5e7eb' : 'none',
                      '&:hover': {
                        bgcolor: selectedSide === s
                          ? undefined
                          : s === 'buy' ? '#f0fdf4' : '#fef2f2',
                      },
                    }}
                  >
                    {s === 'buy'
                      ? <TrendingUpIcon sx={{ fontSize: 13 }} />
                      : <TrendingDownIcon sx={{ fontSize: 13 }} />}
                    {s}
                  </Box>
                ))}
              </Box>

              {/* ── LAYER 2: ORDER CATEGORY ─────────────────────────────── */}
              <Box sx={{ borderBottom: '1px solid #e5e7eb', bgcolor: '#fafafa' }}>
                <Tabs
                  value={activeCategory}
                  onChange={(_, v) => handleCategoryChange(v)}
                  variant="fullWidth"
                  TabIndicatorProps={{ style: { backgroundColor: activeColor, height: 2 } }}
                  sx={{
                    minHeight: 32,
                    '& .MuiTab-root': {
                      minHeight: 32,
                      py: 0,
                      fontSize: '0.68rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: '#6b7280',
                      transition: 'color 0.15s',
                    },
                    '& .Mui-selected': {
                      color: `${activeColor} !important`,
                      fontWeight: 700,
                    },
                  }}
                >
                  {CATEGORIES.map(cat => (
                    <Tab key={cat} value={cat} label={CATEGORY_LABELS[cat]} />
                  ))}
                </Tabs>
              </Box>

              {/* ── LAYER 3: ORDER TYPE ─────────────────────────────────── */}
              <Box sx={{ borderBottom: '1px solid #e5e7eb', bgcolor: '#f9fafb' }}>
                <Tabs
                  value={orderType}
                  onChange={(_, v) => {
                    if (!NOT_YET_IMPLEMENTED_ORDER_TYPES.has(v)) setOrderType(v);
                  }}
                  variant="fullWidth"
                  TabIndicatorProps={{ style: { backgroundColor: activeColor, height: 3 } }}
                  sx={{
                    minHeight: 36,
                    '& .MuiTab-root': {
                      minHeight: 36,
                      py: 0,
                      px: 0.5,
                      fontSize: '0.66rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: '#6b7280',
                      transition: 'color 0.15s',
                    },
                    '& .Mui-selected': {
                      color: `${activeColor} !important`,
                      fontWeight: 700,
                    },
                    '& .Mui-disabled': {
                      opacity: '0.4 !important',
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  {(ORDER_CATEGORY_MAP[activeCategory] as readonly string[]).map(ot => {
                    const isNYI = NOT_YET_IMPLEMENTED_ORDER_TYPES.has(ot);
                    const meta = ORDER_TYPE_META[ot] ?? { label: ot, hint: '' };

                    const label = meta.label;

                    return isNYI ? (
                      <Tab key={ot} value={ot} label={label} disabled
                        sx={{ flex: 1, opacity: '0.38 !important', fontSize: '0.66rem', minHeight: 36, py: 0, px: 0.5, textTransform: 'none' }}
                      />
                    ) : (
                      <Tab key={ot} value={ot} label={label}
                        sx={{ flex: 1, fontSize: '0.66rem', minHeight: 36, py: 0, px: 0.5, textTransform: 'none' }}
                      />
                    );
                  })}
                </Tabs>
              </Box>

              {/* ── LAYER 4: ORDER TYPE HINT + FIELDS ──────────────────── */}
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 }, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, overflowY: 'auto' }}>
                {/* Hint line */}
                {ORDER_TYPE_META[orderType] && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                    {ORDER_TYPE_META[orderType].hint}
                  </Typography>
                )}

                {/* Price fields */}
                <Stack spacing={1}>

                  {/* Limit price */}
                  {needsLimitPrice && (
                    <Box>
                      <TextField
                        fullWidth
                        label="Limit Price"
                        type="number"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        size="small"
                        inputProps={{ step: '0.05' }}
                        InputProps={{
                          startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>₹</Typography>,
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                      />
                      {/* Quick price helpers */}
                      {currentPrice && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          {[
                            { label: 'LTP', action: 'ltp' as const },
                            { label: 'LTP+0.5%', action: 'plus' as const },
                            { label: 'LTP−0.5%', action: 'minus' as const },
                          ].map(({ label, action }) => (
                            <Chip
                              key={action}
                              label={label}
                              size="small"
                              clickable
                              onClick={() => applyPriceHelper(action)}
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Stop price */}
                  {needsStopPrice && (
                    <TextField
                      fullWidth
                      label="Stop / Trigger Price"
                      type="number"
                      value={stopPrice}
                      onChange={e => setStopPrice(e.target.value)}
                      size="small"
                      inputProps={{ step: '0.05' }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>₹</Typography>,
                      }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                    />
                  )}

                  {/* Trailing amount */}
                  {isTrailing && (
                    <TextField
                      fullWidth
                      label="Trailing Amount (₹)"
                      type="number"
                      value={trailingAmount}
                      onChange={e => setTrailingAmount(e.target.value)}
                      size="small"
                      inputProps={{ step: '0.05' }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>₹</Typography>,
                      }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                    />
                  )}

                  {/* Bracket fields */}
                  {isBracket && (
                    <>
                      <TextField
                        fullWidth label="Entry Price" type="number" value={price}
                        onChange={e => setPrice(e.target.value)} size="small" inputProps={{ step: '0.05' }}
                        InputProps={{ startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>₹</Typography> }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                      />
                      <TextField
                        fullWidth label="Profit Target (offset ₹)" type="number" value={profitTarget}
                        onChange={e => setProfitTarget(e.target.value)} size="small" inputProps={{ step: '0.05' }}
                        helperText="Points above entry"
                        InputProps={{ startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>+₹</Typography> }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, '& .MuiFormHelperText-root': { fontSize: '0.6rem' } }}
                      />
                      <TextField
                        fullWidth label="Stop Loss (offset ₹)" type="number" value={stopLoss}
                        onChange={e => setStopLoss(e.target.value)} size="small" inputProps={{ step: '0.05' }}
                        helperText="Points below entry"
                        InputProps={{ startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem' }}>−₹</Typography> }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, '& .MuiFormHelperText-root': { fontSize: '0.6rem' } }}
                      />
                    </>
                  )}

                  {/* Iceberg disclosed qty */}
                  {isIceberg && (
                    <TextField
                      fullWidth label="Disclosed Quantity" type="number" value={disclosedQuantity}
                      onChange={e => setDisclosedQuantity(e.target.value)} size="small" inputProps={{ step: '1', min: '1' }}
                      helperText="Visible quantity per slice (max 10 legs)"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, '& .MuiFormHelperText-root': { fontSize: '0.6rem' } }}
                    />
                  )}

                  {/* Market order info */}
                  {isMarket && currentPrice && (
                    <Box sx={{ bgcolor: '#f0f9ff', borderRadius: 1, px: 1, py: 0.75, border: '1px solid #bae6fd' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#0369a1' }}>
                        Will execute at best available price ≈ {formatCurrency(currentPrice)}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Time in Force */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    TIME IN FORCE
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {TIME_IN_FORCE_OPTIONS
                      .filter(o => ['day', 'gtc', 'ioc', 'fok'].includes(o.value))
                      .filter(o => {
                        const validTif = ORDER_TYPE_VALID_TIF[orderType];
                        return !validTif || validTif.values.includes(o.value);
                      })
                      .map(opt => (
                        <Chip
                          key={opt.value}
                          label={opt.label}
                          size="small"
                          clickable
                          onClick={() => setTimeInForce(opt.value)}
                          sx={{
                            fontSize: '0.6rem',
                            height: 20,
                            fontWeight: timeInForce === opt.value ? 600 : 400,
                            border: '1px solid',
                            borderColor: timeInForce === opt.value ? activeColor : '#e5e7eb',
                            bgcolor: timeInForce === opt.value ? (buyingSide ? '#f0fdf4' : '#fef2f2') : '#f9fafb',
                            color: timeInForce === opt.value ? activeColor : 'text.secondary',
                          }}
                        />
                      ))
                    }
                  </Box>
                </Box>

              </CardContent>
            </Card>

            {/* ════════════════════════════════════════════════════════════════
              ACCOUNT SELECTION — Full-width row below
          ════════════════════════════════════════════════════════════════ */}
            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, gridColumn: '1 / -1' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 }, display: 'flex', flexDirection: 'column', gap: 0.75 }}>

                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountBalanceIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      ACCOUNTS
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={allEligibleSelected}
                        indeterminate={someEligibleSelected}
                        onChange={handleSelectAll}
                        disabled={eligibleAccounts.length === 0}
                        sx={{ p: '2px', '& .MuiSvgIcon-root': { fontSize: 14 } }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        All ({selectedAccountIds.length}/{eligibleAccounts.length})
                      </Typography>
                    }
                  />
                </Box>

                {/* Account table */}
                {accounts.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: '0.65rem', py: 0.5 }}>
                    No broker accounts found. Connect a broker account first.
                  </Alert>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    {/* Header row */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr 1.4fr 60px 90px 72px 90px',
                        alignItems: 'center',
                        px: 0.75,
                        py: 0.4,
                        borderBottom: '1px solid #e5e7eb',
                        bgcolor: '#f3f4f6',
                        borderRadius: '4px 4px 0 0',
                      }}
                    >
                      {['', 'Broker', 'Account / ID', 'Status', 'Balance', 'Qty', 'Est. Value'].map((h, i) => (
                        <Typography key={i} variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>

                    {/* Data rows */}
                    {accounts.map(account => {
                      const connected = isConnected(account);
                      const eligible = isEligible(account);
                      const isSelected = selectedAccountIds.includes(account.id);
                      const qty = accountQuantities[account.id];
                      const estValue = qty && currentPrice ? parseFloat(qty) * currentPrice : null;
                      const disabledReason = !connected
                        ? 'Account is disconnected'
                        : !eligible
                          ? `${account.broker_name} does not support ${ORDER_TYPE_META[orderType]?.label ?? orderType} orders`
                          : null;

                      const dataRow = (
                        <Box
                          key={account.id}
                          onClick={() => eligible && handleAccountToggle(account.id)}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '28px 1fr 1.4fr 60px 90px 72px 90px',
                            alignItems: 'center',
                            px: 0.75,
                            py: 0.5,
                            borderBottom: '1px solid #f3f4f6',
                            cursor: eligible ? 'pointer' : 'not-allowed',
                            opacity: eligible ? 1 : 0.38,
                            bgcolor: isSelected
                              ? (buyingSide ? '#f0fdf4' : '#fef2f2')
                              : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': { bgcolor: eligible && !isSelected ? '#f9fafb' : undefined },
                            '&:last-of-type': { borderBottom: 'none', borderRadius: '0 0 4px 4px' },
                          }}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={!eligible}
                            onChange={() => handleAccountToggle(account.id)}
                            onClick={e => e.stopPropagation()}
                            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 }, color: activeColor, '&.Mui-checked': { color: activeColor } }}
                          />

                          {/* Broker */}
                          <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {account.broker_name}
                          </Typography>

                          {/* Account name + ID */}
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.68rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                              {account.account_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                              {account.broker_account_id}
                            </Typography>
                          </Box>

                          {/* Status */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: connected ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: connected ? '#10b981' : '#9ca3af', textTransform: 'capitalize' }}>
                              {connected ? 'Active' : 'Off'}
                            </Typography>
                          </Box>

                          {/* Balance */}
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#374151' }}>
                            {formatCurrency(account.balance || 0)}
                          </Typography>

                          {/* Qty input */}
                          <Box onClick={e => e.stopPropagation()}>
                            {isSelected && eligible ? (
                              <Box>
                                <TextField
                                  type="number"
                                  value={accountQuantities[account.id] ?? ''}
                                  onChange={e => handleQuantityChange(account.id, e.target.value)}
                                  size="small"
                                  placeholder="0"
                                  error={!!qtyErrors[account.id]}
                                  inputProps={{ min: 1, step: 1 }}
                                  sx={{
                                    width: 64,
                                    '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.35, px: 0.75, textAlign: 'right' },
                                    '& .MuiOutlinedInput-root': { height: 24 },
                                  }}
                                />
                                {qtyErrors[account.id] && (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', fontSize: '0.55rem', color: 'error.main', mt: 0.25, lineHeight: 1.2, maxWidth: 90 }}
                                  >
                                    {qtyErrors[account.id]}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>—</Typography>
                            )}
                          </Box>

                          {/* Est. value */}
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isSelected ? activeColor : '#9ca3af', fontWeight: isSelected ? 600 : 400 }}>
                            {estValue != null ? formatCurrency(estValue) : '—'}
                          </Typography>
                        </Box>
                      );

                      return disabledReason ? (
                        <Tooltip key={account.id} title={disabledReason} placement="top" arrow>
                          <span>{dataRow}</span>
                        </Tooltip>
                      ) : (
                        <span key={account.id}>{dataRow}</span>
                      );
                    })}
                  </Box>
                )}

                {/* Total exposure */}
                {selectedAccountIds.length > 0 && currentPrice && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                        Total exposure
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 700, color: activeColor }}>
                        {formatCurrency(totalQty * currentPrice)}
                      </Typography>
                    </Box>
                  </>
                )}

              </CardContent>
            </Card>

          </Box>
        )}
      </DialogContent>

      {/* ── Order summary + actions ─────────────────────────────────────── */}
      < Divider />
      <DialogActions sx={{ px: 2, py: 1, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Live summary — hidden when result panel is showing */}
        {!showResults && orderSummary && (
          <Box
            sx={{
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              width: '100%',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#475569', fontFamily: 'monospace', letterSpacing: 0.3 }}>
              {orderSummary}
            </Typography>
          </Box>
        )}

        {showResults ? (
          /* Result mode: Place Another + Done */
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <Button
              onClick={() => setShowResults(false)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', px: 2 }}
            >
              Place Another
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              size="small"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                px: 2.5,
                bgcolor: activeColor,
                '&:hover': { bgcolor: buyingSide ? '#15803d' : '#b91c1c' },
              }}
            >
              Done
            </Button>
          </Box>
        ) : (
          /* Order form mode: Cancel + Place Order */
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <Button
              onClick={onClose}
              disabled={placingOrder}
              size="small"
              sx={{ fontSize: '0.7rem', px: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePlaceOrder}
              variant="contained"
              disabled={placingOrder || selectedAccountIds.length === 0}
              startIcon={placingOrder ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : null}
              size="small"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                px: 2.5,
                bgcolor: activeColor,
                '&:hover': { bgcolor: buyingSide ? '#15803d' : '#b91c1c' },
                '&:disabled': { bgcolor: '#d1d5db' },
              }}
            >
              {placingOrder
                ? 'Placing…'
                : `${buyingSide ? 'BUY' : 'SELL'}${selectedAccountIds.length > 1 ? ` (${selectedAccountIds.length})` : ''}`}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog >
  );
}
